/**
 * MediaPicker — Modal zur Auswahl bereits registrierter Media-Docs.
 *
 * Nutzt bestehenden `GET /api/admin/media?media_type=X` mit optionaler
 * clientseitiger Search (Title + Filename substring). Kein Uppy-Upload im
 * Modal (Phase 2b Scope) — Nutzer lädt neue Medien weiterhin unter
 * /admin/medien hoch und wählt sie hier aus.
 *
 * Anwendung:
 *   const [pickerCtx, setPickerCtx] = useState<{type?: string|string[], cb: (id,path)=>void} | null>(null);
 *   ...
 *   <MediaPicker ctx={pickerCtx} onClose={() => setPickerCtx(null)} />
 */
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X, Search, Play } from "lucide-react";
import { api } from "@/lib/auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL as string;

type MediaDoc = {
    id: string;
    title?: string;
    original_filename?: string;
    media_type: string;
    stored_url?: string;
    poster_url?: string;
    size_bytes?: number;
    origin_path?: string;
    width?: number; height?: number;
};

const TYPE_LABEL: Record<string, string> = {
    photo: "Foto", photo_360: "360° Foto",
    video: "Video", video_360: "360° Video",
    fisheye: "Fisheye", tiny_planet: "Tiny Planet",
    before: "Vorher", after: "Nachher",
};
const ALL_TABS: { key: string; label: string }[] = [
    { key: "", label: "Alle" },
    { key: "photo", label: "Foto" },
    { key: "photo_360", label: "360° Foto" },
    { key: "video", label: "Video" },
    { key: "video_360", label: "360° Video" },
];

export type PickerContext = {
    type?: string | string[];
    onPick: (mediaId: string, path: string) => void;
} | null;

export default function MediaPicker({
    ctx, onClose,
}: {
    ctx: PickerContext;
    onClose: () => void;
}) {
    const [items, setItems] = useState<MediaDoc[]>([]);
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState<string>("");
    const [search, setSearch] = useState("");

    useEffect(() => {
        if (!ctx) return;
        if (Array.isArray(ctx.type)) {
            setTab(ctx.type[0] || "");
        } else if (typeof ctx.type === "string") {
            setTab(ctx.type);
        } else {
            setTab("");
        }
    }, [ctx]);

    useEffect(() => {
        if (!ctx) return;
        void (async () => {
            setLoading(true);
            const qs = tab ? `?media_type=${encodeURIComponent(tab)}` : "";
            const res = await api<{ items: MediaDoc[]; count: number }>(`/api/admin/media${qs}`);
            setLoading(false);
            if (res.ok && res.data) setItems(res.data.items);
        })();
    }, [ctx, tab]);

    // ESC-Close
    useEffect(() => {
        if (!ctx) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [ctx, onClose]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return items;
        return items.filter((m) => {
            const hay = [m.title, m.original_filename, m.origin_path].join(" ").toLowerCase();
            return hay.includes(q);
        });
    }, [items, search]);

    const allowedTypes = useMemo<Set<string> | null>(() => {
        if (!ctx?.type) return null;
        if (Array.isArray(ctx.type)) return new Set(ctx.type);
        return new Set([ctx.type]);
    }, [ctx]);

    if (!ctx || typeof document === "undefined") return null;

    return createPortal(
        <div
            role="dialog"
            aria-modal="true"
            aria-label="Medien auswählen"
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            style={{ background: "hsl(var(--hf-ink) / 0.55)" }}
            onClick={onClose}
            data-testid="media-picker-modal"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-6xl max-h-[90vh] rounded-md overflow-hidden flex flex-col"
                style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))" }}
            >
                {/* Header */}
                <div
                    className="px-6 py-5 flex items-center justify-between"
                    style={{ borderBottom: "1px solid hsl(var(--hf-line))" }}
                >
                    <div>
                        <div className="hf-eyebrow" style={{ fontSize: 10 }}>Medien</div>
                        <h2 className="font-display text-2xl md:text-3xl">Medium auswählen</h2>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="relative">
                            <Search size={14} className="absolute left-2.5 top-2.5 text-hf-muted" />
                            <input
                                type="search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Suchen…"
                                className="pl-8 pr-3 py-2 rounded-sm border text-sm bg-white"
                                style={{ borderColor: "hsl(var(--hf-line))" }}
                                data-testid="media-picker-search"
                            />
                        </div>
                        <a
                            href="/admin/medien"
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs px-3 py-2 rounded-sm border"
                            style={{ borderColor: "hsl(var(--hf-line))", background: "hsl(var(--hf-surface))" }}
                        >
                            + Neu hochladen ↗
                        </a>
                        <button
                            type="button"
                            aria-label="Schließen"
                            onClick={onClose}
                            data-testid="media-picker-close"
                            className="p-2 rounded-sm hover:bg-hf-surface"
                        >
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="px-6 py-3 flex flex-wrap gap-2" style={{ borderBottom: "1px solid hsl(var(--hf-line))" }}>
                    {ALL_TABS.map((t) => {
                        const disabled = allowedTypes !== null && t.key !== "" && !allowedTypes.has(t.key);
                        const active = tab === t.key;
                        return (
                            <button
                                key={t.key || "all"}
                                type="button"
                                disabled={disabled}
                                onClick={() => setTab(t.key)}
                                data-testid={`media-picker-tab-${t.key || "all"}`}
                                className="text-xs px-3 py-1.5 rounded-sm border disabled:opacity-30"
                                style={{
                                    background: active ? "hsl(var(--hf-ink))" : "hsl(var(--hf-bg))",
                                    color: active ? "hsl(var(--hf-bg))" : "hsl(var(--hf-ink))",
                                    borderColor: "hsl(var(--hf-line))",
                                }}
                            >
                                {t.label}
                            </button>
                        );
                    })}
                </div>

                {/* Grid */}
                <div className="flex-1 overflow-auto p-6">
                    {loading ? (
                        <div className="text-sm text-hf-muted">Lade Medien…</div>
                    ) : filtered.length === 0 ? (
                        <div className="text-sm text-hf-muted">Keine Medien gefunden.</div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                            {filtered.map((m) => {
                                const isVideo = m.media_type.startsWith("video");
                                const thumbUrl = isVideo
                                    ? (m.poster_url || m.stored_url)
                                    : (m.stored_url || `${BACKEND_URL}/api/media/${m.id}`);
                                const path = m.origin_path || m.stored_url || "";
                                const disabled = allowedTypes !== null && !allowedTypes.has(m.media_type);
                                return (
                                    <button
                                        key={m.id}
                                        type="button"
                                        disabled={disabled}
                                        onClick={() => {
                                            ctx.onPick(m.id, path);
                                            onClose();
                                        }}
                                        data-testid={`media-picker-item-${m.id}`}
                                        className="text-left group disabled:opacity-25 disabled:cursor-not-allowed"
                                    >
                                        <div
                                            className="relative aspect-[3/2] overflow-hidden rounded-sm bg-hf-surface"
                                            style={{ border: "1px solid hsl(var(--hf-line))" }}
                                        >
                                            {thumbUrl && (
                                                <img
                                                    src={thumbUrl}
                                                    alt={m.title || m.original_filename}
                                                    className="w-full h-full object-cover"
                                                />
                                            )}
                                            {isVideo && (
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <Play size={32} className="text-white drop-shadow-lg" />
                                                </div>
                                            )}
                                            <span
                                                className="absolute top-1.5 right-1.5 text-[9px] tracking-[0.16em] uppercase px-1.5 py-0.5 rounded-sm"
                                                style={{
                                                    background: "hsl(var(--hf-ink) / 0.75)",
                                                    color: "hsl(var(--hf-gold-soft))",
                                                }}
                                            >
                                                {TYPE_LABEL[m.media_type] || m.media_type}
                                            </span>
                                        </div>
                                        <div className="mt-1.5 text-xs text-hf-ink truncate" title={m.original_filename}>
                                            {m.title || m.original_filename}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
}
